import {
  Text,
  Banner,
  GreyGrey80,
  IconLock,
  InputSelectControlField,
  Primary13Regular,
  Primary13SemiBold,
  RadioGroup,
  Skeleton,
  SwitchLineInput,
  Tooltip,
  Box,
  Flex
} from '@sequencehq/core-components'
import { dateTimeWithFormat, formatScheduleEndDate } from '@sequencehq/utils'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { FC } from 'react'
import { match } from 'ts-pattern'
import { BillingSchedulePreviewCard } from 'BillingSchedules/index'
import { usePermissions } from 'lib/rbac'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { StartDatePicker } from 'modules/Cube/components/StartDatePicker'
import { phaseDateFormat } from 'modules/Cube/cube.constants'
import {
  changeRenewalModeAtom,
  eligibleSchedulesAtom,
  endExistingScheduleAtom,
  hasApplicableScheduleAtom,
  isRenewalLoadingAtom,
  newScheduleStartDateAtom,
  renewalDateAtom,
  renewalDateValidationErrorsAtom,
  renewalModeAtom,
  RenewalScheduleMode,
  renewalStartMaxDateAtom,
  renewalStartMinDateAtom,
  resetBillingDayLabelAtom,
  selectedScheduleAtom,
  selectedScheduleIdAtom
} from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/executeQuote/executeQuote.atoms'

export const ExecuteQuoteSelectStep: FC = () => {
  const [newScheduleStartDate, setNewScheduleStartDate] = useAtom(
    newScheduleStartDateAtom
  )
  const mode = useAtomValue(renewalModeAtom)
  const changeMode = useSetAtom(changeRenewalModeAtom)
  const schedules = useAtomValue(eligibleSchedulesAtom)
  const hasApplicableSchedule = useAtomValue(hasApplicableScheduleAtom)
  const { can } = usePermissions()
  const flags = useFeatureFlags()

  // Adding a renewal to an existing schedule edits that billing schedule, so it
  // requires billing-schedule edit rights (e.g. finance, not sales) — mirroring
  // the Billing:Update gate enforced on the execute endpoint's append path.
  const canEditBillingSchedule = can('org.billing:update')
  const canAddToExisting = hasApplicableSchedule && canEditBillingSchedule

  // Disabled either because the caller lacks billing-edit permission or because
  // the customer has no schedule to append to. Permission takes precedence in the
  // tooltip. Derived via match to avoid a nested ternary.
  const addToExistingDisabledReason = match({
    canEditBillingSchedule,
    hasApplicableSchedule
  })
    .with(
      { canEditBillingSchedule: false },
      () =>
        'You need billing schedule edit permissions to add a renewal to an existing schedule.'
    )
    .with(
      { hasApplicableSchedule: false },
      () => 'This customer has no schedules that this renewal can be added to.'
    )
    .otherwise(() => undefined)

  // Ending an existing schedule edits it too, so it takes the same permission.
  // Any of the customer's schedules can be ended, so unlike "add to existing"
  // this doesn't depend on whether the renewal would fit onto one.
  const endExistingDisabledReason = match({
    canEditBillingSchedule,
    hasSchedules: schedules.length > 0
  })
    .with(
      { canEditBillingSchedule: false },
      () =>
        'You need billing schedule edit permissions to end an existing schedule.'
    )
    .with(
      { hasSchedules: false },
      () => 'This customer has no schedules that can be ended.'
    )
    .otherwise(() => undefined)

  // The extra content shown under each radio option depends on the selected
  // mode. Derived via an exhaustive match so a new RenewalScheduleMode forces
  // this to be handled rather than silently rendering nothing.
  const modeContent = match(mode)
    .with('existing', () => ({
      existing: canAddToExisting ? <ExistingScheduleSelector /> : undefined,
      new: undefined
    }))
    .with('new', () => ({
      existing: undefined,
      new: (
        <Flex flexDir="column" gap="16px" paddingTop="8px">
          <StartDatePicker
            startDate={newScheduleStartDate}
            onStartDateChange={setNewScheduleStartDate}
          />
          {flags.useEndExistingScheduleForRenewalQuotes && (
            <EndExistingScheduleToggle
              disabledReason={endExistingDisabledReason}
            />
          )}
        </Flex>
      )
    }))
    .exhaustive()

  return (
    <>
      <Box {...Primary13SemiBold} color={GreyGrey80} marginBottom="16px">
        How would you like to execute this quote?
      </Box>
      <RadioGroup
        name="renewalScheduleMode"
        data-testid="modal.executeQuote.renewalMode"
        value={mode}
        onChange={value => changeMode(value as RenewalScheduleMode)}
        options={[
          {
            label: 'Add to existing schedule',
            value: 'existing',
            disabled: Boolean(addToExistingDisabledReason),
            disabledReason: addToExistingDisabledReason,
            // Flag the permission-gated case with a lock (kept full-opacity so it
            // reads as a deliberate RBAC affordance, not dimmed-out text).
            labelAnnotation: canEditBillingSchedule ? undefined : <IconLock />,
            disabledLabelAnnotationOpacity: 1,
            description:
              schedules.length > 1
                ? "Append this quote's phases to one of the current billing schedules."
                : "Append this quote's phases to the current billing schedule.",
            additionalComponent: modeContent.existing
          },
          {
            label: 'Create new schedule',
            value: 'new',
            description: 'Start a separate billing schedule for this contract.',
            additionalComponent: modeContent.new
          }
        ]}
      />
    </>
  )
}

const EndExistingScheduleToggle: FC<{ disabledReason: string | undefined }> = ({
  disabledReason
}) => {
  const [endExistingSchedule, setEndExistingSchedule] = useAtom(
    endExistingScheduleAtom
  )
  const schedules = useAtomValue(eligibleSchedulesAtom)

  const isDisabled = Boolean(disabledReason)
  const hasMultipleSchedules = schedules.length > 1

  return (
    <Flex flexDir="column" gap="12px">
      <Tooltip
        isDisabled={!isDisabled}
        label={disabledReason}
        shouldWrapChildren={false}
        data-testid="modal.executeQuote.endExistingSchedule.disabledReason"
      >
        <Box opacity={isDisabled ? 0.5 : 1}>
          <SwitchLineInput
            data-testid="modal.executeQuote.endExistingSchedule"
            text="End existing schedule when creating a new one"
            value={endExistingSchedule}
            onChange={setEndExistingSchedule}
            isDisabled={isDisabled}
            switchSide="right"
            allowMultiLineLabel
          />
        </Box>
      </Tooltip>
      {endExistingSchedule && (
        <Flex flexDir="column" gap="12px">
          {hasMultipleSchedules && <ScheduleSelectField />}
          <SelectedSchedulePreview data-testid="modal.executeQuote.endExistingPreview" />
        </Flex>
      )}
    </Flex>
  )
}

const SelectedSchedulePreview: FC<{ 'data-testid': string }> = ({
  'data-testid': dataTestId
}) => {
  const selected = useAtomValue(selectedScheduleAtom)
  const isLoadingSchedules = useAtomValue(isRenewalLoadingAtom)

  if (isLoadingSchedules) {
    return <Skeleton height="114px" width="100%" />
  }

  if (!selected) {
    return null
  }

  return (
    <BillingSchedulePreviewCard
      scheduleId={selected.schedule.id}
      customerId={selected.schedule.customerId}
      showViewScheduleButton={false}
      onClick={() =>
        window.open(
          `/billing-schedules/${selected.schedule.id}`,
          '_blank',
          'noopener,noreferrer'
        )
      }
      data-testid={dataTestId}
    />
  )
}

const ScheduleSelectField: FC = () => {
  const schedules = useAtomValue(eligibleSchedulesAtom)
  const [selectedScheduleId, setSelectedScheduleId] = useAtom(
    selectedScheduleIdAtom
  )

  return (
    <InputSelectControlField
      data-testid="modal.executeQuote.scheduleSelect"
      value={selectedScheduleId ?? ''}
      onChange={setSelectedScheduleId}
      placeholder="Select a schedule"
      justifyLabels="space-between"
      options={schedules.map(({ schedule }) => ({
        value: schedule.id,
        label: `${dateTimeWithFormat(
          schedule.startDate,
          phaseDateFormat
        )} - ${formatScheduleEndDate(schedule.endDate ?? '')}`,
        secondaryLabel:
          (schedule.prices[0]?.currency as string | undefined) ?? ''
      }))}
    />
  )
}

const ExistingScheduleSelector: FC = () => {
  const schedules = useAtomValue(eligibleSchedulesAtom)
  const selected = useAtomValue(selectedScheduleAtom)
  const [renewalDate, setRenewalDate] = useAtom(renewalDateAtom)
  const renewalStartMinDate = useAtomValue(renewalStartMinDateAtom)
  const renewalStartMaxDate = useAtomValue(renewalStartMaxDateAtom)
  const renewalDateValidationErrors = useAtomValue(
    renewalDateValidationErrorsAtom
  )
  const resetBillingDayLabel = useAtomValue(resetBillingDayLabelAtom)

  const hasMultipleSchedules = schedules.length > 1
  const isBlocked = Boolean(
    selected && !selected.isViableSchedule && !selected.isMilestone
  )

  return (
    <Flex flexDir="column" gap="8px" paddingTop="12px">
      {selected?.isMilestone && (
        <Banner
          variant="error"
          data-testid="modal.executeQuote.milestoneWarning"
        >
          Milestone-based schedules can't be renewed.
        </Banner>
      )}
      {isBlocked && (
        <Banner variant="error" data-testid="modal.executeQuote.blockedWarning">
          {selected?.blockedValidationMessages.map(message => (
            <Text key={message} {...Primary13Regular} color="inherit">
              {message}
            </Text>
          ))}
        </Banner>
      )}
      {selected?.resetRequired && (
        <Banner variant="warning" data-testid="modal.executeQuote.resetWarning">
          We'll set the billing date for the new phases to match the renewal
          start date
          {resetBillingDayLabel ? ` (${resetBillingDayLabel})` : ''}.
        </Banner>
      )}
      {hasMultipleSchedules && <ScheduleSelectField />}
      <SelectedSchedulePreview data-testid="modal.executeQuote.combinedPreview" />
      {!selected?.isMilestone && (
        <Box paddingTop="8px">
          <StartDatePicker
            label={
              selected?.isOpenEnded
                ? 'Renewal start date'
                : 'Renewal start date (optional)'
            }
            dataTestid="modal.executeQuote.renewalDate"
            startDate={renewalDate}
            onStartDateChange={setRenewalDate}
            minDate={renewalStartMinDate}
            maxDate={renewalStartMaxDate}
            validationErrors={renewalDateValidationErrors}
          />
        </Box>
      )}
    </Flex>
  )
}
